import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Paper, Typography } from "@mui/material";
import { CalendarDate, getLocalTimeZone, startOfMonth, today } from "@internationalized/date";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { BadgePercent, Book, Calendar, ChartColumn, CircleDot, Eye, FileText, Inbox, PiggyBank, TrendingUp, } from "lucide-react";
import { useLayout } from "../../contexts/LayoutContext";
import { DASHBOARD_HEADER_HEIGHT } from "../../layout/DashboardHeader";
import { ROUTES } from "../../routes/routes.constants";
import { ApiCall } from "../../helper/axios";
import CommonLoader from "../../common_components/CommonLoader";
import { Dropdown } from "../../common_components/FormInputs";
import CustomMonthComparePicker from "../../common_components/daterangepicker/CustomMonthComparePicker";
import InfoTooltip from "../../common_components/InfoTooltip";
import VarianceBadge from "../../common_components/VarianceBadge";
import EmployeeAccountsModal from "./EmployeeAccountsModal";
import EmployeeAccountPerformance from "./EmployeeAccountPerformance";
import ClockifyCalendar from "./ClockifyCalendar";
import EmployeeBonusSummary, { bonusLevelFor } from "./EmployeeBonusSummary";
import ExportLoadingButton from "./ExportLoadingButton";
import { toDesignationList } from "../../utils/designationFilters";
import exportEmployeeDetails from "./exportEmployeeDetails";
import { toast } from "../../common_components/Toast/ToastUtils";
const MODULE_KEY = "employee_performance";
// AM designation badge (keyed by the API's user.type), coloured like the listing's AM badge.
const ROLE_BADGE = {
    paid_media: { label: "Paid Media AM", bg: "#F4F3FF", color: "#6938EF" },
    affiliate: { label: "Affiliate AM", bg: "#EFF8FF", color: "#175CD3" },
    "affiliate,paid_media": { label: "Both", bg: "#F0FDF9", color: "#107569" },
    both: { label: "Both", bg: "#F0FDF9", color: "#107569" },
};
// Badge for the picked person's designation (users.user_type). The three AM designations keep their coloured
// badge ("Full Services AM" reads "Both", as before); any other designation gets a neutral grey one.
const AM_DESIGNATION_BADGE = {
    "paid media am": ROLE_BADGE.paid_media,
    "affiliate am": ROLE_BADGE.affiliate,
    "full services am": ROLE_BADGE.both,
};
// A person can hold several designations: an AM one decides the coloured badge, otherwise the grey badge lists them all.
const designationBadge = (userType) => {
    const list = toDesignationList(userType);
    if (list.length === 0)
        return null;
    const am = list.find((d) => AM_DESIGNATION_BADGE[d.toLowerCase()]);
    if (am)
        return AM_DESIGNATION_BADGE[am.toLowerCase()];
    return { label: list.join(", "), bg: "#F2F4F7", color: "#344054" };
};
const ICON_SIZE = 18;
// Static card layout; `apiKey` points at the matching entry in `data.cards`.
const CARD_META = [
    { key: "total_accounts", apiKey: "total_accounts", format: "count", title: "Total Accounts", icon: _jsx(Book, { size: ICON_SIZE }), tooltip: "Total accounts handled in the selected month.", viewData: true, modalTitle: "Total Account Overview" },
    { key: "new_accounts", apiKey: "new_accounts", format: "count", title: "New Accounts", icon: _jsx(TrendingUp, { size: ICON_SIZE }), tooltip: "Accounts that started in the selected month.", viewData: true, modalTitle: "Total New Account Overview" },
    { key: "lost_accounts", apiKey: "lost_accounts", format: "count", title: "Lost Accounts", icon: _jsx(Inbox, { size: ICON_SIZE }), tooltip: "Accounts that were terminated in the selected month.", viewData: true, modalTitle: "Total Lost Account Overview", fallIsGood: true },
    { key: "monthly_placements", apiKey: "monthly_placements", format: "whole", title: "Monthly Placements", icon: _jsx(CircleDot, { size: ICON_SIZE }) },
    { key: "avg_mrr", apiKey: "avg_mrr", format: "money", title: "Avg MRR", icon: _jsx(FileText, { size: ICON_SIZE }), prefix: "$" },
    { key: "total_mgmt_fees", apiKey: "total_mgmt_fees", format: "money", title: "Total Mgmt Fees", icon: _jsx(PiggyBank, { size: ICON_SIZE }), prefix: "$" },
    { key: "total_commissions", apiKey: "total_commissions", format: "money", title: "Total Commissions", icon: _jsx(BadgePercent, { size: ICON_SIZE }), prefix: "$" },
    { key: "commission_pct", apiKey: "commission_pct_of_mgmt_fees", format: "whole", title: "Commission % of Mgmt Fees", icon: _jsx(ChartColumn, { size: ICON_SIZE }), tooltip: "Total commissions as a percentage of total management fees.", suffix: "%" },
];
const formatNumber = (n, format) => {
    if (n === null || n === undefined)
        return "-";
    if (format === "money")
        return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (format === "whole")
        return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
    return n.toLocaleString("en-US", { maximumFractionDigits: 1 });
};
// `zero` = the person has no records: every card reads 0 (no compare / change badge) instead of "-".
const buildCards = (apiCards, zero = false) => CARD_META.map(({ apiKey, format, ...meta }) => {
    if (zero)
        return { ...meta, value: formatNumber(0, format), compareValue: null, changePercent: null };
    const c = apiCards?.[apiKey];
    return {
        ...meta,
        value: formatNumber(c?.value, format),
        compareValue: c && c.compare_value !== null && c.compare_value !== undefined ? formatNumber(c.compare_value, format) : null,
        changePercent: c?.change_percent ?? null,
    };
});
// Header status pill, picked by the Bonus Summary's Total Bonus Value (<= 40, over 40 up to 100, over 100).
const STATUS_BY_LEVEL = {
    low: { label: "At Risk", color: "#D92D20", bg: "#FEF3F2" },
    mid: { label: "On Track", color: "#DC6803", bg: "#FFFAEB" },
    high: { label: "Excellent", color: "#079455", bg: "#ECFDF3" },
};
// ═══════════════ HELPERS ═══════════════
const previousMonth = (m) => startOfMonth(m.subtract({ months: 1 }));
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const toApiMonth = (m) => `${String(m.month).padStart(2, "0")}/${m.year}`;
// The listing passes its month as "MM/YYYY" (same format the API uses).
const parseApiMonth = (apiMonth) => {
    if (!apiMonth)
        return null;
    const [m, y] = apiMonth.split("/").map(Number);
    if (!m || !y)
        return null;
    return new CalendarDate(y, m, 1);
};
const FONT_FAMILY = "Inter, sans-serif";
// Grey "$" / "%" next to the number, same treatment as the listing page.
const SymbolText = ({ children, size, gap }) => (_jsx("span", { style: { color: "#98A2B3", fontWeight: 400, fontSize: size, marginRight: gap, marginLeft: gap }, children: children }));
// ═══════════════ SUB-COMPONENTS ═══════════════
const StatusPill = ({ label, color, bg }) => (_jsxs(Box, { sx: {
        display: "inline-flex", alignItems: "center", gap: 0.5,
        bgcolor: bg, color, borderRadius: "16px", px: 1.25, py: 0.25,
    }, children: [_jsx(CircleDot, { size: 12 }), _jsx(Typography, { sx: { fontFamily: FONT_FAMILY, fontSize: "12px", fontWeight: 500, color: "inherit", lineHeight: 1.5 }, children: label })] }));
const DetailMetricCard = React.memo(({ card, onViewData }) => (_jsxs(Paper, { elevation: 0, sx: {
        border: "1px solid #EAECF0", borderRadius: "8px", bgcolor: "#fff",
        p: "10px 12px", display: "flex", flexDirection: "column", gap: 1, minWidth: 0,
    }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, minWidth: 0 }, children: [_jsx(Box, { sx: { display: "flex", color: "#667085", flexShrink: 0 }, children: card.icon }), _jsx(Typography, { sx: { fontFamily: FONT_FAMILY, fontSize: "14px", fontWeight: 500, color: "#475467", lineHeight: "20px" }, children: card.title }), card.tooltip && _jsx(InfoTooltip, { title: card.tooltip, placement: "top" })] }), card.viewData && (_jsxs(Box, { onClick: onViewData, sx: { display: "flex", alignItems: "center", gap: 0.75, color: "#667085", cursor: "pointer", flexShrink: 0 }, children: [_jsx(Eye, { size: 16 }), _jsx(Typography, { sx: { fontFamily: FONT_FAMILY, fontSize: "14px", color: "inherit", lineHeight: "20px" }, children: "View Data" })] }))] }), _jsxs(Typography, { sx: { fontFamily: FONT_FAMILY, fontSize: "14px", fontWeight: 700, color: "#101828", lineHeight: "20px" }, children: [card.prefix && card.value !== "-" && _jsx(SymbolText, { size: "14px", gap: "2px", children: card.prefix }), card.value, card.suffix && card.value !== "-" && _jsx(SymbolText, { size: "14px", gap: "4px", children: card.suffix })] }), card.compareValue !== null && (_jsxs(_Fragment, { children: [_jsx(Box, { sx: { height: "1px", bgcolor: "#EAECF0" } }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }, children: [_jsxs(Typography, { sx: { fontFamily: FONT_FAMILY, fontSize: "14px", color: "#667085", lineHeight: "20px" }, children: [card.prefix && card.compareValue !== "-" && _jsx(SymbolText, { size: "14px", gap: "2px", children: card.prefix }), card.compareValue, card.suffix && card.compareValue !== "-" && _jsx(SymbolText, { size: "14px", gap: "3px", children: card.suffix })] }), card.changePercent !== null && (_jsx(VarianceBadge, { value: card.changePercent, favourable: card.fallIsGood ? true : undefined }))] })] }))] })));
// ═══════════════ PAGE ═══════════════
export default function EmployeePerformanceDetails() {
    const location = useLocation();
    const navigate = useNavigate();
    const { setIsSidebarExpanded } = useLayout();
    // Selected AM + month come from the listing (AM link click).
    const routeState = (location.state || {});
    const amName = routeState.name || "";
    const [month, setMonth] = useState(() => parseApiMonth(routeState.month) ?? startOfMonth(today(getLocalTimeZone())));
    const [compareMonth, setCompareMonth] = useState(() => previousMonth(parseApiMonth(routeState.month) ?? startOfMonth(today(getLocalTimeZone()))));
    // Clockify button -> the Clockify calendar popover (anchored to the button).
    const [clockifyAnchor, setClockifyAnchor] = useState(null);
    const token = useSelector((state) => state.auth.token);
    // The "Bonus Summary" action: without it the Bonus Summary section, its status pill and its export sheet are hidden.
    const canSeeBonus = useSelector((state) => (state.auth.permissions?.employee_performance?.actions || []).includes("Bonus Summary"));
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true); // true from the first render so the loader shows before the fetch effect runs
    const [error, setError] = useState(null);
    // The picked person has no Employee Performance data (e.g. not an account manager) -> "No records found".
    const [noRecords, setNoRecords] = useState(false);
    const requestId = useRef(0); // ignores a slow response that was overtaken by a newer month pick
    // Name dropdown: the AM whose page was opened (selected by default) + everyone on their employee portfolio.
    // The list stays anchored to that first AM while the user switches between people.
    const ownerId = routeState.ownerId || routeState.userId;
    const [amList, setAmList] = useState([]);
    // Loaded fresh every time (no cache): on open, whenever the picked person changes, and when this tab regains focus
    // - so a change made in another tab (e.g. someone removed from the employee portfolio) shows up here.
    useEffect(() => {
        if (!ownerId)
            return;
        let cancelled = false;
        const load = async () => {
            try {
                const response = await ApiCall("POST", "/api/employee-performance/am-list", { user_id: ownerId }, { Authorization: token || "", "x-module-key": MODULE_KEY });
                const body = response?.data ?? response?.response?.data;
                if (cancelled || !body?.success)
                    return;
                const list = body.data || [];
                setAmList(list);
                // The picked person is no longer on the owner's portfolio -> back to the default (the AM whose page this is).
                const stillListed = list.some((am) => am.user_id === routeState.userId);
                if (routeState.userId && routeState.userId !== ownerId && !stillListed) {
                    const owner = list.find((am) => am.user_id === ownerId);
                    navigate(location.pathname, {
                        replace: true,
                        state: { ...routeState, ownerId, userId: ownerId, name: owner?.name || routeState.name || "" },
                    });
                }
            }
            catch {
                // The dropdown just keeps the current AM when the list can't load.
            }
        };
        load();
        window.addEventListener("focus", load);
        return () => {
            cancelled = true;
            window.removeEventListener("focus", load);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ownerId, routeState.userId]);
    // The current AM is always an option, even before the list arrives.
    const amOptions = useMemo(() => {
        const options = amList.map((am) => ({ value: am.user_id, label: am.name }));
        if (routeState.userId && !options.some((o) => o.value === routeState.userId)) {
            options.unshift({ value: routeState.userId, label: amName });
        }
        return options;
    }, [amList, routeState.userId, amName]);
    // Designation of the person picked in the dropdown (works for people who are not account managers too).
    const selectedUserType = amList.find((am) => am.user_id === routeState.userId)?.user_type ??
        (routeState.userId && details?.user?.user_id === routeState.userId ? details.user.user_type : undefined);
    const selectedBadge = designationBadge(selectedUserType);
    // Clockify button: off for someone with no Clockify account linked, or with no records at all.
    const clockifyAvailable = !noRecords && (amList.find((am) => am.user_id === routeState.userId)?.has_clockify ?? true);
    // Picking another AM re-points this same page at them (route state), so refresh and back keep working.
    const handleSelectAm = (userId) => {
        if (!userId || userId === routeState.userId)
            return;
        const name = amList.find((am) => am.user_id === userId)?.name || "";
        navigate(location.pathname, { replace: true, state: { ...routeState, ownerId, userId, name } });
    };
    // Fetch the cards whenever the AM / month / compare month changes.
    useEffect(() => {
        if (!routeState.userId || !month)
            return;
        const id = ++requestId.current;
        const load = async () => {
            setLoading(true);
            setError(null);
            setNoRecords(false);
            try {
                const response = await ApiCall("POST", "/api/employee-performance/details", {
                    user_id: routeState.userId,
                    month: toApiMonth(month),
                    ...(compareMonth ? { compare_month: toApiMonth(compareMonth) } : {}),
                }, { Authorization: token || "", "x-module-key": MODULE_KEY });
                if (id !== requestId.current)
                    return;
                const body = response?.data ?? response?.response?.data;
                if (body?.success) {
                    setDetails(body.data);
                }
                else if (body?.status_code === 404) {
                    setDetails(null);
                    setNoRecords(true);
                }
                else {
                    setDetails(null);
                    setError(body?.message || "Failed to load Employee Performance details.");
                }
            }
            catch (e) {
                if (id !== requestId.current)
                    return;
                setDetails(null);
                setError(e?.message || "Failed to load Employee Performance details.");
            }
            finally {
                if (id === requestId.current)
                    setLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeState.userId, month, compareMonth]);
    const cards = buildCards(details?.cards, noRecords);
    // "View Data" modal: which card's client list is open (null = closed).
    const [viewKey, setViewKey] = useState(null);
    const viewCard = cards.find((c) => c.key === viewKey);
    const viewRows = (viewKey ? details?.cards[viewKey]?.list : undefined) ?? [];
    const viewMonthLabel = (() => {
        const [m, y] = (details?.month || "").split("/").map(Number);
        return m && y ? `${MONTH_SHORT[m - 1]}, ${y}` : "";
    })();
    // Export: the cards / bonus / client lists already on screen + the Account Performance table. That table is its
    // own component, so it is requested again here (Redis-cached on the server, so this is normally instant).
    const [exporting, setExporting] = useState(false);
    const handleExport = async () => {
        if (exporting)
            return;
        if (!details || !month) {
            toast.info("No data available to export");
            return;
        }
        setExporting(true);
        try {
            const response = await ApiCall("POST", "/api/employee-performance/account-performance", { user_id: routeState.userId, month: toApiMonth(month) }, { Authorization: token || "", "x-module-key": MODULE_KEY });
            const body = response?.data ?? response?.response?.data;
            if (!body?.success) {
                toast.info(body?.message || "Failed to export. Please try again.");
                return;
            }
            exportEmployeeDetails({
                name: details.user?.name || amName,
                month: details.month,
                compareMonth: details.compare_month,
                cards: cards.map((c) => ({
                    title: c.title,
                    value: `${c.prefix && c.value !== "-" ? c.prefix : ""}${c.value}${c.suffix && c.value !== "-" ? c.suffix : ""}`,
                    compareValue: c.compareValue === null ? null : `${c.prefix && c.compareValue !== "-" ? c.prefix : ""}${c.compareValue}${c.suffix && c.compareValue !== "-" ? c.suffix : ""}`,
                    changePercent: c.changePercent,
                })),
                bonus: canSeeBonus ? details.bonus ?? null : null,
                accounts: {
                    total: (details.cards.total_accounts?.list ?? []),
                    new: (details.cards.new_accounts?.list ?? []),
                    lost: (details.cards.lost_accounts?.list ?? []),
                },
                accountPerformance: (body.data?.accounts ?? []),
            });
        }
        catch {
            toast.info("Failed to export. Please try again.");
        }
        finally {
            setExporting(false);
        }
    };
    // Account Performance: "Clockify not connected - Click here" links this person's Clockify account (same server sync as
    // the Edit User drawer's refresh button), then the table is requested again.
    const [accountReload, setAccountReload] = useState(0);
    const handleConnectClockify = async () => {
        const targetId = routeState.userId;
        if (!targetId)
            return false;
        try {
            const response = await ApiCall("POST", `/api/users/${targetId}/sync-clockify-id`, {}, { Authorization: token || "" });
            const body = response?.data ?? response?.response?.data;
            if (body?.success && body?.data?.clockify_user_id) {
                setAmList((list) => list.map((am) => (am.user_id === targetId ? { ...am, has_clockify: true } : am)));
                setAccountReload((n) => n + 1);
                toast.success("Clockify connected.");
                return true;
            }
            toast.error(body?.message || "Could not connect Clockify.");
        }
        catch {
            toast.error("Could not connect Clockify. Please try again.");
        }
        return false;
    };
    // Collapse the sidebar on page load (same as Publisher Reporting) to give the page more room.
    useEffect(() => {
        setIsSidebarExpanded(false);
    }, [setIsSidebarExpanded]);
    // Opened directly (no AM picked from the listing) - send the user back to the listing.
    if (!routeState.userId) {
        return _jsx(Navigate, { to: ROUTES.employeePerformance.path, replace: true });
    }
    return (_jsxs(Box, { sx: {
            position: "relative",
            width: "100%",
            bgcolor: "#FAFAFA",
            minHeight: `calc(100vh - ${DASHBOARD_HEADER_HEIGHT}px)`,
            // While loading, lock the page to exactly one screen so nothing behind the loader can scroll.
            ...(loading && { height: `calc(100vh - ${DASHBOARD_HEADER_HEIGHT}px)`, overflow: "hidden" }),
        }, children: [loading && (_jsx(Box, { sx: {
                    position: "absolute", inset: 0, bgcolor: "#fff", zIndex: 100,
                    display: "flex", alignItems: "center", justifyContent: "center",
                }, children: _jsx(CommonLoader, { className: "table-loader" }) })), _jsxs(Box, { sx: { p: 2, display: "flex", flexDirection: "column", gap: 1 }, children: [_jsxs(Box, { sx: {
                            bgcolor: "#fff", border: "1px solid #EAECF0", borderRadius: "8px",
                            px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap",
                        }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }, children: [_jsx(Box, { sx: { minWidth: "200px" }, className: "client-Dropdown", children: _jsx(Dropdown, { value: routeState.userId || "", options: amOptions, onChange: (v) => handleSelectAm(Array.isArray(v) ? v[0] : v), placeholder: "Select Account Manager", searchable: true, disabled: amOptions.length <= 1 }) }), selectedBadge && (_jsx(Box, { sx: { bgcolor: selectedBadge.bg, color: selectedBadge.color, borderRadius: "16px", px: 1.5, py: 0.25 }, children: _jsx(Typography, { sx: { fontFamily: FONT_FAMILY, fontSize: "12px", fontWeight: 500, color: "inherit", lineHeight: 1.5 }, children: selectedBadge.label }) })), canSeeBonus && details?.bonus && _jsx(StatusPill, { ...STATUS_BY_LEVEL[bonusLevelFor(details.bonus.total_bonus)] })] }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }, children: [_jsx(CustomMonthComparePicker, { value: month, compareValue: compareMonth, onApply: ({ main, compare }) => {
                                            setMonth(main);
                                            setCompareMonth(compare);
                                        }, placeholder: "Select Month" }), _jsx(Button, { variant: "outlined", disabled: !clockifyAvailable, disableFocusRipple // closing the calendar hands focus back to this button; no pink focus pulse
                                        : true, onClick: (e) => setClockifyAnchor(e.currentTarget), startIcon: _jsx(Calendar, { size: 18 }), sx: {
                                            fontSize: "13px", color: "#181D27", borderColor: "#D0D5DD", textTransform: "none",
                                            fontWeight: 500, borderRadius: "8px", height: "36px", px: 2,
                                            // The theme's primary is pink, which MUI would paint on hover / focus - keep it a neutral outlined button.
                                            "&:hover, &.Mui-focusVisible": { bgcolor: "#F9FAFB", borderColor: "#D0D5DD" },
                                            // "&&" raises the specificity above App.css's global "body .MuiButton-colorPrimary.Mui-disabled" (pink fill).
                                            "&&.Mui-disabled": { color: "#A4A7AE", borderColor: "#E9EAEB", backgroundColor: "#FAFAFA", cursor: "not-allowed", pointerEvents: "auto" },
                                        }, children: "Clockify" }), _jsx(ExportLoadingButton, { loading: exporting, onClick: handleExport })] })] }), error && _jsx(Alert, { severity: "error", sx: { borderRadius: "8px", alignItems: "center" }, children: error }), _jsx(Box, { sx: {
                            display: "grid", gap: 1,
                            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
                        }, children: cards.map((card) => (_jsx(DetailMetricCard, { card: card, onViewData: card.viewData ? () => setViewKey(card.key) : undefined }, card.key))) }), month && (_jsx(EmployeeAccountPerformance, { userId: routeState.userId, month: toApiMonth(month), 
                        // Only for a person who has data here and whose Clockify link is known to be missing.
                        clockifyConnected: noRecords ? undefined : amList.find((am) => am.user_id === routeState.userId)?.has_clockify, onConnectClockify: handleConnectClockify, reloadKey: accountReload })), canSeeBonus && _jsx(EmployeeBonusSummary, { bonus: details?.bonus, noRecords: noRecords })] }), _jsx(ClockifyCalendar, { open: !!clockifyAnchor, anchorEl: clockifyAnchor, onClose: () => setClockifyAnchor(null), userId: routeState.userId, month: month }), _jsx(EmployeeAccountsModal, { open: !!viewCard, onClose: () => setViewKey(null), title: viewCard?.modalTitle || "", monthLabel: viewMonthLabel, rows: viewRows })] }));
}
